import { takeEvery, put } from "redux-saga/effects";
import {
  getLoginRespoonse, getLoginResponseSuccess, getActiveUsers, getActiveUsersSuccess, logoutUser,
  logoutUserSuccess, forgotPinFirstResponse, forgotPinFirstResponseSuccess, validateEmail,
  validateEmailSuccess, switchUser, switchUserSuccess, fetchOfflineUsersSuccess,
  fetchOfflineUsers, getLatestVersion, saveFetchLastestVersionData,
  fetchOfflineUsersCallComplete, fetchMasterDataPicklist, saveMasterDataPicklistSuccess,
} from "../../../slices/LoginSlice";
import api from "../../../../utils/apiService";
import { ENDPOINT } from "../../../../config/endpoints";
import { getLocalStorage, removeLocalStorage, setLocalStorage } from "../../../../utils/localStorage";
import { getUserDataObj } from "../../../slices/BusinessSlice";
import { saveOthersOffline, checkOfflineOperatedSales } from "../../../../helpers/OfflineHelper";
import { getLoginMetaInfo } from "../../../../helpers/commonHelpers";
import qs from "qs";
import { verifyPinRequest, verifyPinSuccess, verifyPinFailure } from "../../../slices/LoginSlice";
import { LIST_TYPES, LOGIN_TYPES, METHODS } from "../../../../data/constants";
import { startCartAuditSession, endCartAuditSession } from "../../../../helpers/cartAuditHelper";
import { isBetaEnv } from "../../../../data/envConstants";
import { logout as handleLogoutAction } from "../../../../effects-bridge";

export * from "./shared";

/**
 * WEB variant of `redux-store/saga/login/login`. See `./shared.ts` for why nothing is shared.
 *
 * ⚠️ **`handleLogoutAction` is the effects bridge's `logout`, imported under the app's own name**
 * so the call sites are unchanged. This app wires it to its `handleLogoutAction`, which talks to
 * a native Android bridge — see effects-bridge.ts.
 *
 * ⚠️ `checkOfflineOperatedSales` is the startup sweep that drops stale local sales and flips the
 * terminal offline if any still need syncing. It runs on LOGIN, once — session 26 deferred its
 * uplift for exactly this closure, and this file is its only caller.
 */

function* getLoginInfo(action: any): any {
  const payload = action.payload;
  const metaInfo = getLoginMetaInfo({ loginType: LOGIN_TYPES.drkLogin });
  try {
    const requestData = {
      pin: payload?.pin,
      uid: payload.uid,
      metaInfo: metaInfo ? JSON.stringify(metaInfo) : undefined,
    };
    const response = yield api.request({
      method: "POST",
      headers: {
        vn: process.env.REACT_APP_VN,
        clientDate: new Date().toISOString(),
        "Content-Type": "application/x-www-form-urlencoded",
      },
      url: ENDPOINT.LOGIN.getLogin(),
      data: qs.stringify(requestData),
    } as any);
    if (response?.ATOKEN && response.isPinExpired) {
      setLocalStorage("RESET_ATOKEN", response.ATOKEN);
    } else if (response?.ATOKEN) {
      if (getLocalStorage("isShowOfflineModeToggle")) {
        checkOfflineOperatedSales();
      }
      setLocalStorage("ATOKEN", response.ATOKEN);
      yield put(fetchMasterDataPicklist({ listType: LIST_TYPES.PHONE_COUNTRIES }));
      // Hand the fresh token to the shell's cart-audit uploader (release_4.0.0). Electron gets
      // the typed preload method; an NW.js shell that predates it still answers the postMessage.
      const electronAPI = (window as any).electronAPI;
      if (electronAPI?.cartAudit?.setToken) {
        electronAPI.cartAudit.setToken({
          token: response.ATOKEN,
          baseUrl: process.env.REACT_APP_RISTA_BASE_API_URL,
        });
      } else {
        window.parent.postMessage({
          detail: {
            key: 'chrome.cartAudit',
            action: 'setToken',
            message: { token: response.ATOKEN, baseUrl: process.env.REACT_APP_RISTA_BASE_API_URL}
          }
        }, '*');
      }
      const staffId = response?.userId || payload?.uid || "";
      if (staffId) {
        startCartAuditSession(staffId);
      }
    }
    yield put(getLoginResponseSuccess(response));
  } catch (error) {
    // console.log('error', error);
  }
}

function* fetchActiveUsers(): any {
  try {
    const response = yield api.request({
      method: "GET",
      headers: { deviceIdentifier: getLocalStorage("deviceIdentifier") || "" },
      url: ENDPOINT.LOGIN.getActiveUsers(),
    } as any);

    yield put(getActiveUsersSuccess(response));
    yield saveOthersOffline({
      type: 'user',
      items: response
    });
  } catch (error) {
    // console.log('error', error);
  }
}

function* fetchLogoutResponse(): any {
  // console.log("logoutInfo!!");
  try {
    const response = yield api.request({
      method: "POST",
      headers: { deviceIdentifier: getLocalStorage("deviceIdentifier") || "" },
      url: ENDPOINT.LOGIN.logoutUser(),
    } as any);
    if (!response?.code) {
      removeLocalStorage('ATOKEN')
    }
    if (!!response.data) {
      // console.log("response logoutInfo: ", response);
      yield put(logoutUserSuccess(response));
    } else {
      yield put(logoutUserSuccess({ status: "success" }));
    }
    const electronAPI = (window as any).electronAPI;
    if (electronAPI?.cartAudit?.clearToken) {
      electronAPI.cartAudit.clearToken();
    } else {
      window.parent.postMessage({
        detail: {
          key: "chrome.cartAudit",
          action: "clearToken",
          message: null
        },
      }, "*");
    }
    // ADD THIS: Only end session on EXPLICIT logout
    endCartAuditSession();
    handleLogoutAction();
  } catch (error) {
    // console.log('error', error);
  }
}

function* fetchForgotPinFirstResponse(action: any): any {
  try {
    const response = yield api.request({
      method: "POST",
      url: ENDPOINT.LOGIN.forgotPin(),
      data: action?.payload,
      customUrl: true,
    } as any);
    yield put(forgotPinFirstResponseSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

function* fetchValidateEmailResponse(action: any): any {
  try {
    let response = yield api.request({
      method: "POST",
      url: ENDPOINT.LOGIN.forgotPin(),
      data: action?.payload,
      customUrl: true
    } as any);
    if (!response) {
      response = {
        status: "success",
      };
    }
    yield put(validateEmailSuccess(response));
  } catch (error) {
    console.log("error", error);
  }
}

function formatAuthError(expiryCheckResponse: any) {
  const { code, message, leftAttempts } = expiryCheckResponse;
  let normalizedMessage = message;
  if (typeof message === "string") {
    normalizedMessage = { message };
  }
  return {
    code,
    message: normalizedMessage?.message || "Invalid PIN/Password. Please try again.",
    leftAttempts: normalizedMessage?.leftAttempts ?? leftAttempts,
  };
}
function* switchUserApi(action: any): any {
  let payload = action.payload
  const metaInfo = getLoginMetaInfo({ loginType: LOGIN_TYPES.drkLogin });
  try {
    const requestData = {
      pin: payload?.pin,
      uid: payload.uid,
      metaInfo: metaInfo ? JSON.stringify(metaInfo) : undefined,
    };
    try {
      const expiryCheckResponse = yield api.request({
        method: 'POST',
        headers: { vn: process.env.REACT_APP_VN, "Content-Type": "application/x-www-form-urlencoded" },
        url: ENDPOINT.LOGIN.checkPinExpiry(),
        data: qs.stringify(requestData)
      } as any);
      if (expiryCheckResponse?.code === 'REST_AUTHENTICATION_FAILED') {
        const errorResponse = formatAuthError(expiryCheckResponse);
        yield put(switchUserSuccess(errorResponse));
        yield payload?.callback && payload.callback(errorResponse);
        return;
      }
      if (expiryCheckResponse?.isExpired === true) {
        const errorResponse = {
          code: 'PIN_EXPIRED',
          message: "This user's PIN/Password has expired. Please reset it before switching.",
          isExpired: true
        };
        yield put(switchUserSuccess(errorResponse));
        yield payload?.callback && payload.callback(errorResponse);
        return;
      }
      if (expiryCheckResponse?.isExpired === false) {
        const response = yield api.request({
          method: 'POST',
          headers: { vn: process.env.REACT_APP_VN, "Content-Type": "application/x-www-form-urlencoded" },
          url: ENDPOINT.LOGIN.getLogin(),
          data: qs.stringify(requestData)
        } as any);
        if(response?.ATOKEN) {
          setLocalStorage('ATOKEN', response.ATOKEN);
          yield put(getUserDataObj({ isRedirect: true }));
          const staffId = response?.userId || payload?.uid || '';
          if (staffId) {
            startCartAuditSession(staffId);
          }
        }
        yield put(switchUserSuccess(response));
        yield payload?.callback && payload.callback(response);
        return;
      }
    } catch (expiryError) {
      const errorResponse = {
        code: 'EXPIRY_CHECK_FAILED',
        message: "Unable to verify PIN status. Please try again.",
      };
      yield put(switchUserSuccess(errorResponse));
      yield payload?.callback && payload.callback(errorResponse);
      return;
    }
  } catch (error) {
    // console.log('error', error);
  }
}

function* fetchOfflineUsersAPI(): any {
  try {
    const response = yield api.request({
      method: 'GET',
      url: ENDPOINT.LOGIN.fetchOfflineUserList()
    } as any);
    if (!response?.code) {
      yield put(fetchOfflineUsersSuccess(response));
      setLocalStorage("offlineuser", JSON.stringify(response));
    }
  } catch (error) {
    // console.log('error', error);
  } finally {
    yield put(fetchOfflineUsersCallComplete());
  }
}

function* fetchLastestVersion(action: any): any {
  const payload = action.payload;
  const data = {
    version: payload.currentVersion,
    os: payload.currentPlatform,
    isNew: true,
    isBeta: isBetaEnv
  }
  try {
    const response = yield api.request({
      method: 'post',
      url: ENDPOINT.LOGIN.fetchLatestVersion(),
      data: qs.stringify(data),
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },

    } as any)
    yield put(saveFetchLastestVersionData(response))
  } catch (error) {
    console.log('errorData', error)
  }
}
function* verifyPinSaga(action: any): any {
  const { uid, pin, callback } = action.payload;
  try {
    const response = yield api.request({
      method: METHODS.POST,
      headers: { 
        "Content-Type": "application/x-www-form-urlencoded",
      },
      url: ENDPOINT.LOGIN.verifyPin(),
      data: qs.stringify({ uid, pin }),
    } as any);

    yield put(verifyPinSuccess(response));
    if (callback) callback(response);
    
  } catch (error) {
    yield put(verifyPinFailure());
    if (callback) callback({ error: true, message: "Network Error" });
  }
}

function* fetchMasterDataPicklistSaga(action: any): any {
  const { listType } = action.payload;
  try {
    const response = yield api.request({
      method: 'GET',
      url: ENDPOINT.POS.getMasterDataPicklist(listType)
    } as any);
    
    yield put(saveMasterDataPicklistSuccess(response));
  } catch (error) {
    console.log('error fetching master data picklist:', error);
  }
}

export default function* root(): any {
  yield takeEvery(getLoginRespoonse.type, getLoginInfo);
  yield takeEvery(getActiveUsers.type, fetchActiveUsers);
  yield takeEvery(logoutUser.type, fetchLogoutResponse);
  yield takeEvery(forgotPinFirstResponse.type, fetchForgotPinFirstResponse);
  yield takeEvery(validateEmail.type, fetchValidateEmailResponse);
  yield takeEvery(switchUser.type, switchUserApi)
  yield takeEvery(fetchOfflineUsers.type, fetchOfflineUsersAPI)
  yield takeEvery(getLatestVersion.type, fetchLastestVersion)
  yield takeEvery(verifyPinRequest.type, verifyPinSaga);
  yield takeEvery(fetchMasterDataPicklist.type, fetchMasterDataPicklistSaga);
}
